import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { WatchProgressEntry } from "@hybrid-iptv/types";

/**
 * Watch progress (Sections 9–10 Continue Watching + resume), scoped to the
 * active local viewer profile. Profiles share the store but never the view.
 */
// See favoritesStore: the active profile owns the row, and profile #1 keeps
// the historic "local" id so existing resume points are never orphaned.
import { activeProfileId, DEFAULT_PROFILE_ID } from "../settings/profileStore.js";

export interface ProgressInput {
  contentType: "vod" | "episode" | "catchup";
  contentRef: string;
  positionSeconds: number;
  durationSeconds: number;
  /** Enough to re-play without a catalog lookup (Continue Watching rail). */
  meta?: PlaybackMeta;
  /** Preserve the profile that started playback if the active profile changes. */
  profileId?: string;
}

export interface PlaybackMeta {
  title: string;
  posterUrl?: string;
  /** The catalog's REDACTED url — never a resolved one. playSource hydrates or
   *  tokenizes at play time, so no credential or short-lived token is stored. */
  url: string;
  /**
   * When this stops being playable, for content that expires. Catch-up is only
   * replayable while the programme is inside the provider's archive window;
   * after that the replay URL 404s, and a Continue Watching tile would look
   * fine and play nothing.
   */
  expiresAt?: string;
  /**
   * For an episode: which series it belongs to, as the provider knows it, so "Play next" can find
   * the following episode from wherever the episode was started. The hashed series id inside the
   * episode's key is NOT enough: it did not match the catalog's row on the box (2026-10-13).
   */
  series?: { providerId: string; title: string };
}

/** False once a remembered item has passed its expiry; true when it has none. */
export function stillPlayable(meta: PlaybackMeta | undefined, nowMs: number): boolean {
  if (!meta) return false;
  if (!meta.expiresAt) return true;
  const expires = new Date(meta.expiresAt).getTime();
  return !Number.isFinite(expires) || expires > nowMs;
}

export function watchProgressForProfile(
  entries: WatchProgressEntry[],
  profileId: string,
): WatchProgressEntry[] {
  return entries
    .filter((entry) => entry.profileId === profileId)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

interface ProgressState {
  entries: WatchProgressEntry[];
  /** `${profileId}:${contentRef}` → metadata for that profile's Continue Watching. */
  playbackMeta: Record<string, PlaybackMeta>;
  save: (input: ProgressInput) => void;
  /** Record playback metadata without creating a progress entry (used at play time). */
  remember: (contentRef: string, meta: PlaybackMeta, profileId?: string) => void;
  get: (contentRef: string) => WatchProgressEntry | undefined;
  metaOf: (contentRef: string) => PlaybackMeta | undefined;
  metaFor: (profileId: string, contentRef: string) => PlaybackMeta | undefined;
  continueWatching: () => WatchProgressEntry[];
  clear: (contentRef: string) => void;
}

export function progressKey(profileId: string, contentRef: string): string {
  return `${profileId}:${contentRef}`;
}

export const useProgressStore = create<ProgressState>()(
  persist(
    (set, get) => ({
      entries: [],
      playbackMeta: {},
      save: (input) => {
        const { entries, playbackMeta } = get();
        // Match on profile AND contentRef: two profiles watching the same
        // title must keep independent positions rather than overwrite.
        const profileId = input.profileId ?? activeProfileId();
        const key = progressKey(profileId, input.contentRef);
        const matches = (e: WatchProgressEntry) =>
          e.contentRef === input.contentRef && e.profileId === profileId;
        const existing = entries.some(matches);
        const next: WatchProgressEntry = {
          id: key,
          profileId,
          contentType: input.contentType,
          contentRef: input.contentRef,
          positionSeconds: input.positionSeconds,
          durationSeconds: input.durationSeconds,
          updatedAt: new Date().toISOString(),
        };
        const nextMeta = { ...playbackMeta };
        if (input.meta) nextMeta[key] = input.meta;
        set({
          entries: existing ? entries.map((e) => (matches(e) ? next : e)) : [...entries, next],
          playbackMeta: nextMeta,
        });
      },
      remember: (contentRef, meta, profileId = activeProfileId()) => {
        const key = progressKey(profileId, contentRef);
        set({ playbackMeta: { ...get().playbackMeta, [key]: meta } });
      },
      // Scoped to the active profile: entries for every profile share one
      // persisted array, so an unscoped read leaks another profile's history.
      get: (contentRef) =>
        get().entries.find((e) => e.contentRef === contentRef && e.profileId === activeProfileId()),
      metaOf: (contentRef) => get().playbackMeta[progressKey(activeProfileId(), contentRef)],
      metaFor: (profileId, contentRef) => get().playbackMeta[progressKey(profileId, contentRef)],
      continueWatching: () => watchProgressForProfile(get().entries, activeProfileId()),
      clear: (contentRef) => {
        const key = progressKey(activeProfileId(), contentRef);
        const playbackMeta = { ...get().playbackMeta };
        delete playbackMeta[key];
        set({
          entries: get().entries.filter((e) => !(e.contentRef === contentRef && e.profileId === activeProfileId())),
          playbackMeta,
        });
      },
    }),
    {
      name: "slique-watch-progress",
      version: 1,
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Partial<ProgressState>;
        const playbackMeta = { ...(state.playbackMeta ?? {}) };
        if (version < 1) {
          // Before profile metadata was namespaced it was shared by every
          // profile. Give each item to every profile with progress on it, so
          // no profile's Continue Watching tiles lose what they replay; an
          // item nobody has progress on goes to the original local profile.
          const entries = Array.isArray(state.entries) ? state.entries : [];
          for (const [contentRef, meta] of Object.entries(playbackMeta)) {
            const owners = new Set(
              entries.filter((entry) => entry.contentRef === contentRef).map((entry) => entry.profileId),
            );
            if (owners.size === 0) owners.add(DEFAULT_PROFILE_ID);
            for (const profileId of owners) playbackMeta[progressKey(profileId, contentRef)] = meta;
            delete playbackMeta[contentRef];
          }
        }
        return { ...state, playbackMeta };
      },
    },
  ),
);

/** True when a saved position is worth resuming (mid-way, not near the end). */
export function isResumable(p: WatchProgressEntry | undefined, marginSeconds = 90): boolean {
  if (!p) return false;
  if (p.positionSeconds <= 5) return false;
  if (p.durationSeconds > 0 && p.durationSeconds - p.positionSeconds < marginSeconds) return false;
  return true;
}

/** "1h 04m remaining" style label for a saved position. */
export function formatRemaining(p: WatchProgressEntry): string {
  const remaining = Math.max(0, p.durationSeconds - p.positionSeconds);
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m remaining`;
  if (m > 0) return `${m}m remaining`;
  return "Almost done";
}
